import './auto-credit-order.scss';

import { Alert, notification } from 'antd';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import {
  OrderHistory,
  SystemNotifications
} from '@/components/Shared/entities';
import { ROUTES } from '@/components/Shared/routes/routes';
import { AllurLoader, CustomTab } from '@/components/Shared/ui';
import { DictTypes } from '@/constants';
import { useAppDispatch, useAppSelector } from '@/store';
import { setHeaderConfig } from '@/store/app.slice';
import {
  startGlobalLoadingWithText,
  stopGlobalLoadingWithTextById
} from '@/store/loading.slice';
import {
  setClientInfo,
  setClientInfoFullName,
  setCurrent,
  setNewOrderToInitial
} from '@/store/newOrder.slice';

import { CreateTabContext } from './ExistedOrder';
import {
  ChangeClientInfo,
  DraftFormResetButton,
  HeaderInfo,
  HorizontalInfo
} from './entities';
import useAutoCredRequestId from './hooks/useAutoCredRequestId';
import useGetOrderData from './hooks/useGetOrderData';
import { OrderStep } from './order-step/order-step';

const items = [
  {
    key: 'order',
    title: 'Заявка'
  },
  {
    key: 'offer',
    title: 'Предложения'
  },
  {
    key: 'financing',
    title: 'Финансирование'
  }
];

const NewOrder = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { clientInfo, current, orderApp } = useAppSelector(
    (state) => state.newOrder
  );
  const { dicts } = useAppSelector((state) => state.dict);
  const { requestId } = useAutoCredRequestId();

  const [editModal, showEditModal] = useState<boolean>(false);
  const [historyModal, setHistoryModal] = useState<boolean>(false);

  const { isLoading } = useGetOrderData();

  const isBiometricSessionFailed =
    !!orderApp?.biometricSession &&
    !orderApp.biometricSession.isLoading &&
    (orderApp.biometricSession.isExpired ||
      !orderApp.biometricSession.isValidated);

  useEffect(() => {
    if (!requestId && !location.state?.requestId) {
      navigate(ROUTES.autoCredit);
    }
  }, [navigate, requestId, location.state?.requestId]);

  const handleNextStep = (requestId: string) => {
    dispatch(setCurrent(1));
    navigate(ROUTES.autoCreditRequest(requestId), { replace: true });
  };

  const openEditModal = () => {
    if (
      (orderApp === null || orderApp?.status === 'draft') &&
      !isBiometricSessionFailed
    ) {
      if (current !== 0) return;
      showEditModal(true);
    }
  };

  const handleStepCurrentChange = () => {
    if (isBiometricSessionFailed) return;
    notification.warning({
      title: 'Заполните все поля формы',
      description:
        'Чтобы перейти на данный этап, вам необходимо создать заявку до конца',
      duration: 5
    });
  };

  useEffect(() => {
    if (
      !orderApp ||
      !orderApp.biometricSession ||
      !orderApp.biometricSession.isLoading
    )
      return;

    const loadingId = `biometric-waiting-${orderApp.uuid}`;

    dispatch(
      startGlobalLoadingWithText({
        id: loadingId,
        type: 'layoutBlur',
        text: 'Ожидание завершения биометрической идентификации клиента...'
      })
    );

    return () => {
      dispatch(stopGlobalLoadingWithTextById(loadingId));
    };
  }, [orderApp, orderApp?.biometricSession]);

  useEffect(() => {
    return () => {
      dispatch(setNewOrderToInitial());
      dispatch(setClientInfo(null));
    };
  }, []);

  useEffect(() => {
    dispatch(
      setHeaderConfig({
        title: 'Заявка автокредитования',
        helmetTitle: 'Автокредитование - Новая заявка'
      })
    );
    return () => {
      dispatch(setHeaderConfig({}));
    };
  }, []);

  if (isLoading) {
    return (
      <div key="new-order" className="auto-credit-order">
        <AllurLoader fullScreen />
      </div>
    );
  }

  return (
    <div className="auto-credit-order">
      <div className="auto-credit-order__steps-wrapper">
        <CustomTab
          activeTab={items[current].key}
          setActiveTab={() => handleStepCurrentChange()}
          tabs={items}
        />
      </div>

      <SystemNotifications marginFrom="top" />

      <div className="auto-credit-order__container auto-credit-order__container--new-order">
        <div className="auto-credit-order__client-info">
          <HeaderInfo
            openEditModal={openEditModal}
            setHistoryModal={setHistoryModal}
          />
        </div>

        <HorizontalInfo
          openEditModal={openEditModal}
          setHistoryModal={setHistoryModal}
        />

        {isBiometricSessionFailed && (
          <Alert
            title={
              orderApp.biometricSession?.isExpired
                ? 'Истек срок действия биометрической сессии'
                : dicts?.[DictTypes.Statuses]?.getBy.code[orderApp.status]
                    ?.name || orderApp.status
            }
            type="error"
            showIcon
            className="auto-credit-order__biometric-session-failed-alert"
          />
        )}

        <div className="auto-credit-order__content">
          {current === 0 && (
            <CreateTabContext.Provider
              value={{ isFieldsFrozen: isBiometricSessionFailed }}
            >
              <OrderStep createRequest={handleNextStep} />
            </CreateTabContext.Provider>
          )}
        </div>

        <DraftFormResetButton />
      </div>

      <ChangeClientInfo
        defaultValues={{
          firstName: clientInfo.firstName,
          lastName: clientInfo.lastName,
          middleName: clientInfo.middleName
        }}
        onSave={(values) => {
          dispatch(
            setClientInfoFullName({
              lastName: values.lastName,
              firstName: values.firstName,
              middleName: values.middleName
            })
          );
        }}
        isOpened={editModal}
        onClose={showEditModal}
      />

      <OrderHistory
        historyType="auto-credit"
        isOpened={historyModal}
        onClose={setHistoryModal}
      />
    </div>
  );
};

export default NewOrder;
